import {
  Checkbox,
  ComboboxData,
  Divider,
  Group,
  MultiSelect,
  Paper,
  Stack,
  Text,
  TextInput
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { IconSearch } from '@tabler/icons-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import DataTables from 'components/DataTables';
import React, { useEffect, useState } from 'react';
import { courseService } from 'services/lcms/course.service';
import { showErrorNotification } from 'utils/notification';
import MaterialLink from './MaterialLink';

const PARAMS = {
  pageNumber: 1,
  pageSize: 10000
};

interface ITypeActivity {
  id: number;
  module: string;
  title: string;
  icon: string;
}

interface IActivityByModule {
  cmid: number;
  instance_Id: number;
  instance_Name: string;
  icon: string;
  modid: number;
  modname: string;
  course_name: string;
  activity_url: string;
}

type ModuleData = IActivityByModule & { selected: boolean };

interface IActivityData {
  activityName: string;
  activityId: string;
  activityUrl?: string;
  courseId: string;
}

interface MasterActivitySectionProps {
  onChange: (_data: IActivityData[]) => void;
}

const MasterActivitySection = ({ onChange }: MasterActivitySectionProps) => {
  const [searchValue, setSearchValue] = useState<string>('');
  const [debounced] = useDebouncedValue(searchValue, 1000);
  const [selectedModules, setSelectedModules] = useState<ModuleData[]>([]);
  const [selectedTypeActivity, setSelectedTypeActivity] = useState<string[] | null>([]);
  const [selectedActivity, setSelectedActivity] = useState<IActivityData[] | null>([]);

  const { data, isFetching: isLoadingGetType } = useQuery({
    queryKey: ['lcmsGetTypeActivity', PARAMS],
    queryFn: () => courseService.getTypeActivity({ ...PARAMS })
  });

  const getActivityByModules = useMutation({
    mutationFn: courseService.getActivityByModules,
    onError: (error: any) => {
      showErrorNotification({
        title: 'Error',
        message: error?.message || 'Failed to get activity by modules'
      });
    }
  });

  const { isPending: isLoadingGetActivity, data: dataActivities } = getActivityByModules;

  useEffect(() => {
    if (selectedTypeActivity && selectedTypeActivity.length > 0) {
      getActivityByModules.mutate({ moduleIds: selectedTypeActivity.map((item) => Number(item)) });
    }
  }, [selectedTypeActivity]);

  useEffect(() => {
    if (dataActivities?.data && dataActivities?.data.length > 0) {
      setSelectedModules(
        dataActivities?.data
          .map((item: IActivityByModule) => {
            if (item.instance_Name) {
              return {
                ...item,
                selected: false
              };
            }
            return null;
          })
          .filter((item: IActivityByModule) => item !== null)
      );
    }
  }, [dataActivities?.data]);

  useEffect(() => {
    if (debounced) {
      setSelectedModules((prevData) =>
        prevData.filter(
          (item) => item.instance_Name.toLocaleLowerCase() === debounced.toLocaleLowerCase()
        )
      );
    }
    return () => {
      setSelectedModules(
        dataActivities?.data
          .map((item: IActivityByModule) => {
            if (item.instance_Name) {
              return {
                ...item,
                selected: false
              };
            }
            return null;
          })
          .filter((item: IActivityByModule) => item !== null) || []
      );
    };
  }, [debounced]);

  useEffect(() => {
    if (selectedModules && selectedModules.length > 0) {
      setSelectedActivity(
        selectedModules
          .filter((item) => item.selected)
          .map((item) => ({
            activityName: item.instance_Name,
            activityUrl: item.activity_url,
            activityType: item.modname,
            courseName: item.course_name,
            activityId: item.cmid.toString(),
            courseId: item.modid.toString()
          }))
      );
    }
  }, [selectedModules]);

  useEffect(() => {
    if (selectedActivity && selectedActivity.length > 0) {
      onChange(selectedActivity);
    }
  }, [selectedActivity]);

  const typeActivities: ComboboxData = data?.data?.map((item: ITypeActivity) => ({
    value: item.id.toString(),
    label: item.title + `(${item.module})`
  }));

  const columns = [
    {
      heading: 'Activity Name',
      props: 'instance_Name',
      active: true,
      sortable: true
    },
    {
      heading: 'Activity Type',
      props: 'modname',
      active: true,
      sortable: true
    },
    {
      heading: 'Course name',
      props: 'course_name',
      active: true,
      sortable: true
    },
    {
      heading: 'Beelajar LMS Link',
      props: ({ activity_url }: { activity_url: string }) => (
        <MaterialLink linkMaterial={activity_url} title={'Link'} isBeelajarLoginLink={true} />
      ),
      active: true,
      sortable: false
    },
    {
      heading: '',
      active: true,
      props: (row: IActivityByModule & { selected: boolean }) => {
        return (
          <Checkbox
            defaultChecked={row.selected}
            checked={row.selected}
            onChange={(e) => handleCheckbox(e, row.cmid)}
          />
        );
      }
    }
  ];

  const handleCheckbox = (e: React.ChangeEvent<HTMLInputElement>, id: number) => {
    const { checked } = e.target;
    setSelectedModules((prevData) =>
      prevData.map((item) => (item.cmid === id ? { ...item, selected: checked } : item))
    );
  };

  return (
    <Stack mt={'sm'}>
      <MultiSelect
        label='Select Type Activity'
        data={typeActivities || []}
        disabled={isLoadingGetType}
        placeholder={isLoadingGetType ? 'Loading...' : 'Select type'}
        searchable
        onChange={setSelectedTypeActivity}
        leftSection={<IconSearch />}
      />

      {selectedTypeActivity && selectedTypeActivity.length > 0 && (
        <Paper withBorder p={16} radius={'lg'}>
          <Group justify='space-between'>
            <Text fw={600} fz={'sm'}>
              Found {selectedModules?.length} activities
            </Text>
            <TextInput
              leftSection={<IconSearch size={20} />}
              placeholder='Search activity...'
              onChange={(e) => setSearchValue(e.target.value)}
            />
          </Group>
          <Divider my={16} />
          <DataTables
            data={selectedModules || []}
            columns={columns}
            loading={isLoadingGetActivity}
            emptyMessage='No activity found'
            withBulkData
          />
        </Paper>
      )}
    </Stack>
  );
};

export default MasterActivitySection;
